import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { SOCIAL_LINKS } from "@/components/SocialIcons";

const TIKTOK_HANDLE = "@7pillarsoflifetherapy";

// To feature specific videos, add their full TikTok URLs here.
// They'll render as a responsive grid of official TikTok embeds.
// e.g. "https://www.tiktok.com/@7pillarsoflifetherapy/video/1234567890123456789"
const FEATURED_VIDEO_URLS = [];

function videoIdFromUrl(url) {
  const m = url.match(/\/video\/(\d+)/);
  return m ? m[1] : null;
}

function TikTokEmbed({ url }) {
  const videoId = videoIdFromUrl(url);
  if (!videoId) return null;
  return (
    <blockquote
      className="tiktok-embed"
      cite={url}
      data-video-id={videoId}
      style={{ maxWidth: "100%", minWidth: 0, margin: 0 }}
    >
      <section />
    </blockquote>
  );
}

function loadTikTokScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("tiktok-embed-script")) {
    if (window.tiktokEmbedLoad) window.tiktokEmbedLoad();
    return;
  }
  const s = document.createElement("script");
  s.id = "tiktok-embed-script";
  s.async = true;
  s.src = "https://www.tiktok.com/embed.js";
  document.body.appendChild(s);
}

export default function TikTokFeed() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || FEATURED_VIDEO_URLS.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (inView) loadTikTokScript();
  }, [inView]);

  const hasVideos = FEATURED_VIDEO_URLS.length > 0;

  return (
    <section
      ref={ref}
      data-testid="tiktok-section"
      id="tiktok"
      className="py-24 md:py-32 bg-bone relative overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(#3B2E5A 1px, transparent 1px)",
          backgroundSize: "30px 30px",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-5">Social</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Follow Seven Pillars on <em className="italic text-terracotta">TikTok.</em>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Short, honest moments on relationships, healing, supervision, and the small life-skills that quietly change everything.
            </p>
          </div>
          <a
            href={SOCIAL_LINKS.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tiktok-follow-cta"
            className="inline-flex items-center gap-2 bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-7 py-3.5 font-medium transition-colors w-fit"
          >
            {TIKTOK_HANDLE} <ArrowRight size={16} />
          </a>
        </div>

        {hasVideos ? (
          <div
            data-testid="tiktok-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {FEATURED_VIDEO_URLS.map((u) => (
              <div
                key={u}
                className="rounded-2xl overflow-hidden border border-sage/25 bg-surface min-h-[480px]"
              >
                <TikTokEmbed url={u} />
              </div>
            ))}
          </div>
        ) : (
          <div
            data-testid="tiktok-follow-card"
            className="rounded-2xl border border-sage/25 bg-surface p-10 md:p-14 text-center"
          >
            <p className="font-display text-2xl md:text-3xl text-forest leading-snug max-w-xl mx-auto">
              Fresh reflections, life-skills tips, and supervision conversations — posted regularly.
            </p>
            <a
              href={SOCIAL_LINKS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tiktok-card-follow-btn"
              className="mt-7 inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-3.5 font-medium transition-colors"
            >
              Watch on TikTok <ArrowRight size={16} />
            </a>
            <p className="mt-4 text-xs text-ink/50">{TIKTOK_HANDLE}</p>
          </div>
        )}
      </div>
    </section>
  );
}
